/**
 * Pure helpers and types for the `ai_dynamic_section` question type (spec-01). No React, no I/O -
 * unit-testable directly.
 *
 * The section's authoring config is stored FLAT on the question row, not nested under a wrapper
 * key: `schemas_fastapi/ai_dynamic_section.py::AiDynamicSectionConfig` fields (`goal`, `topics`,
 * `rubric`, `context_bindings`, `media_pool`, `limits`, `allowed_emit_types`, `model`) are all
 * top-level keys on the question dict, exactly as `routes_fastapi/survey.py::_find_dynamic_section`
 * reads them (it returns the question itself, then does `config.get('limits')` etc. directly).
 */

/** One rubric point the prober is trying to cover. Distinct from the unrelated per-question
 *  follow-up `QuestionRubric` in `components/survey/types.ts` (different shape, different feature) -
 *  this one always carries a stable `id`, since coverage rows and `targets_rubric_points` key on it. */
export interface AiDynamicSectionRubricPoint {
  id: string;
  text: string;
  tier: number;
  /** The acceptance bar for this point, AND a probing hint: the backend refuses to mark this
   *  point covered until what it describes is actually said, so this is read at judge time, not
   *  just authoring time. Optional — an unset bar falls back to the backend's own looser default. */
  guidance?: string;
}

/**
 * Each field is nullable: the product owner ruled a researcher may leave any of these unspecified
 * rather than being forced to invent a cap they don't care about. `null` means "not specified,
 * the platform's own default applies" — distinct from `0`, which is a real (if unusual) value a
 * researcher chose (`constants/ai_dynamic_section.py::SectionLimits` is Optional[int] = None on
 * the backend side for the same reason). Never coerce an empty input to `0`.
 */
export interface AiDynamicSectionLimits {
  min_questions: number | null;
  max_questions: number | null;
  advisory_seconds: number | null;
  hard_ceiling_seconds: number | null;
}

export interface AiDynamicSectionMediaAsset {
  asset_id: string;
  s3_key: string;
  description?: string | null;
  relevance?: string | null;
}

export interface AiDynamicSectionContextBindings {
  question_ids: string[];
  block_ids: string[];
}

/** The authoring contract, spec-01 "Authoring contract - AiDynamicSectionConfig". */
export interface AiDynamicSectionConfig {
  goal: string;
  topics: string[];
  rubric: AiDynamicSectionRubricPoint[];
  context_bindings: AiDynamicSectionContextBindings;
  media_pool: AiDynamicSectionMediaAsset[];
  limits: AiDynamicSectionLimits;
  allowed_emit_types: string[];
  model: string;
  /** Section-level guidance on HOW to probe (pacing, when to push harder) — distinct from `goal`,
   *  which says WHAT the section should understand. Read by the prober on every turn. */
  probing_instructions: string;
  /** Set by the backend when the goal, probing instructions, or one of the linked
   *  `context_bindings.question_ids` questions changed after `rubric` was derived from them — the
   *  checklist below no longer reflects what it was built from. Absent/false on a fresh or
   *  up-to-date rubric. The author's ruling was to surface this and let the researcher choose,
   *  never to regenerate silently. */
  rubric_stale?: boolean;
  /** Set by the backend once the rubric derivation moved off the synchronous save path: `pending`
   *  right after a new section is saved (rubric not written yet), `ready` once it has been, or
   *  `failed` if derivation errored. ABSENT (not any of the three) means a row saved before this
   *  field existed — see `effectiveRubricStatus`, which is the only place that is allowed to treat
   *  that absence as anything other than pending. */
  rubric_status?: RubricStatus;
}

/** The three states the backend reports for asynchronous rubric derivation. See
 *  `AiDynamicSectionConfig.rubric_status` and `effectiveRubricStatus`. */
export type RubricStatus = 'pending' | 'ready' | 'failed';

/** The parent survey question row for a section - `id`/`type` plus the (possibly partial, while
 *  mid-authoring) config fields, flat. */
export type AiDynamicSectionQuestion = { id: string; type: 'ai_dynamic_section' } & Partial<AiDynamicSectionConfig>;

/** The platform defaults are always concrete numbers, never null — only an author's OWN choice on
 *  `AiDynamicSectionLimits` can be null (unspecified). Kept as a distinct type so a consumer that
 *  reads a default value (e.g. for helper text) never has to re-null-check it. */
type RequiredSectionLimits = { [K in keyof AiDynamicSectionLimits]: number };

/** Matches `constants/ai_dynamic_section.py::SectionLimits` defaults exactly. */
export const DEFAULT_SECTION_LIMITS: RequiredSectionLimits = {
  min_questions: 3,
  max_questions: 10,
  advisory_seconds: 180,
  hard_ceiling_seconds: 300,
};

/** Matches `constants/ai_dynamic_section.py::DEFAULT_ALLOWED_EMIT_TYPES`. `max_diff` and
 *  `conjoint_analysis` are never offered - a full best-worst or conjoint exercise is far too long
 *  for one probe turn (spec-01 requirement 5, `BANNED_EMIT_TYPES`). */
export const ALLOWED_EMIT_TYPES = [
  'open_text',
  'single_choice',
  'multiple_choice',
  'rating_scale',
  'matrix_rating',
  'percentage_allocation',
] as const;

export type AllowedEmitType = (typeof ALLOWED_EMIT_TYPES)[number];

/**
 * The config a freshly-typed section starts from — every field present, none of them read off a
 * question row that has not been authored yet. `limits` and `allowed_emit_types` mirror the
 * backend's own defaults (`SectionLimits`, `DEFAULT_ALLOWED_EMIT_TYPES`) rather than inventing
 * new ones; everything else starts empty, matching "nothing authored yet".
 */
export const EMPTY_AI_DYNAMIC_SECTION_CONFIG: AiDynamicSectionConfig = {
  goal: '',
  topics: [],
  rubric: [],
  context_bindings: { question_ids: [], block_ids: [] },
  media_pool: [],
  limits: DEFAULT_SECTION_LIMITS,
  allowed_emit_types: [...ALLOWED_EMIT_TYPES],
  model: '',
  probing_instructions: '',
};

/**
 * Reads a survey-builder question row (`ParsedQuestion`, `components/survey/types.ts`) as this
 * section's authoring config.
 *
 * The row types `rubric` as the unrelated `QuestionRubric` object every OTHER question type uses
 * (`{ information_goal, points }`) — genuinely incompatible with this section's own
 * `rubric: AiDynamicSectionRubricPoint[]`. `SurveySession.tsx` already reads the same row through
 * an `as unknown as AiDynamicSectionQuestion` cast at the runtime seam (`renderQuestionInput`);
 * this is the authoring seam's equivalent, so the collision is resolved the same way in both
 * places rather than widening the shared `ParsedQuestion` type to a union every other question
 * type would then have to narrow against.
 */
export function dynamicSectionConfigFromRow(row: unknown): AiDynamicSectionConfig {
  const q = (row ?? {}) as Partial<AiDynamicSectionConfig>;
  return {
    goal: q.goal ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.goal,
    topics: q.topics ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.topics,
    rubric: q.rubric ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.rubric,
    context_bindings: q.context_bindings ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.context_bindings,
    media_pool: q.media_pool ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.media_pool,
    limits: q.limits ?? DEFAULT_SECTION_LIMITS,
    allowed_emit_types: q.allowed_emit_types ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.allowed_emit_types,
    model: q.model ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.model,
    probing_instructions: q.probing_instructions ?? EMPTY_AI_DYNAMIC_SECTION_CONFIG.probing_instructions,
    rubric_stale: q.rubric_stale,
    rubric_status: q.rubric_status,
  };
}

/**
 * Resolves what the checklist area should treat a section as, applying the LEGACY fallback: a row
 * saved before `rubric_status` existed carries no such field at all, and must read as `ready` —
 * never `pending` (a permanent spinner) and never `failed` (a false alarm). Every consumer of
 * `rubric_status` must go through this rather than reading the field directly.
 */
export function effectiveRubricStatus(rubricStatus: RubricStatus | undefined): RubricStatus {
  return rubricStatus ?? 'ready';
}

/** Prefix for an id this file generates for an author-added rubric point — distinct from
 *  whatever scheme the backend's rubric-derivation service uses for its own points (`rp_<n>` in
 *  practice), though `generateRubricPointId` only needs to avoid colliding with what is already
 *  present, not know how those ids were made. */
const LOCAL_RUBRIC_POINT_ID_PREFIX = 'rp_local_';

/**
 * An id for a rubric point the author adds directly in the edit panel. Guaranteed not to equal
 * any id already in `existingIds` (backend-issued or otherwise) — coverage rows reference a point
 * by id, so a collision would silently re-point recorded coverage at the wrong point. This never
 * touches or renumbers an existing id; it only proposes one new id to append.
 */
export function generateRubricPointId(existingIds: readonly string[]): string {
  const used = new Set(existingIds);
  let n = existingIds.length + 1;
  let candidate = `${LOCAL_RUBRIC_POINT_ID_PREFIX}${n}`;
  while (used.has(candidate)) {
    n += 1;
    candidate = `${LOCAL_RUBRIC_POINT_ID_PREFIX}${n}`;
  }
  return candidate;
}

/** Matches `constants/ai_dynamic_section.py` STOP_* constants. */
export const STOP_COVERED = 'covered';
export const STOP_MAX_QUESTIONS = 'max_questions';
export const STOP_TIMEOUT = 'timeout';
export const STOP_ABANDONED = 'abandoned';
export const STOP_ERROR = 'error';

export type DynamicSectionStopReason =
  | typeof STOP_COVERED
  | typeof STOP_MAX_QUESTIONS
  | typeof STOP_TIMEOUT
  | typeof STOP_ABANDONED
  | typeof STOP_ERROR;

/** One emitted turn - the prober's structured output (spec-01 "Runtime contract"). */
export interface DynamicSectionEmittedQuestion {
  turn: number;
  question_id: string;
  type: string;
  text: string;
  options?: string[] | null;
  type_config?: Record<string, unknown> | null;
  media_asset_id?: string | null;
  targets_rubric_points?: string[] | null;
}

export type DynamicSectionNextResponse =
  | { done: true; stop_reason: DynamicSectionStopReason }
  | { question: DynamicSectionEmittedQuestion };

/** Body of `POST .../answer` - echoes the six emitted-question fields plus the respondent's
 *  answer (routes_fastapi/survey.py::dynamic_section_answer reads exactly these six back off
 *  `data`; any `turn`/`question_id` supplied would be ignored, so they are never sent). */
export interface DynamicSectionAnswerPayload {
  type: string;
  text: string;
  options?: string[] | null;
  type_config?: Record<string, unknown> | null;
  media_asset_id?: string | null;
  targets_rubric_points?: string[] | null;
  answer: unknown;
}

export type DynamicSectionAnswerResponse =
  | { status: 'success'; turns_asked: number }
  | { done: true; stop_reason: DynamicSectionStopReason };

/**
 * The number of questions is decided at runtime, so a percentage would be a lie.
 * "of up to N" is honest and still gives the respondent an end in sight.
 */
export function progressLabel(turn: number, maxQuestions: number): string {
  const shown = Math.min(turn, maxQuestions);
  return `Question ${shown} of up to ${maxQuestions}`;
}

/** Reads the section's `max_questions` off the flat question row, falling back to the backend's
 *  own default rather than inventing a different one here. */
export function maxQuestionsFor(question: AiDynamicSectionQuestion): number {
  return question.limits?.max_questions ?? DEFAULT_SECTION_LIMITS.max_questions;
}

/** Echoes the six content fields the emitted question carried, plus the respondent's answer -
 *  the exact shape `/answer` expects. */
export function buildAnswerPayload(
  question: DynamicSectionEmittedQuestion,
  answer: unknown
): DynamicSectionAnswerPayload {
  return {
    type: question.type,
    text: question.text,
    options: question.options,
    type_config: question.type_config,
    media_asset_id: question.media_asset_id,
    targets_rubric_points: question.targets_rubric_points,
    answer,
  };
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Whether `answer` is a complete response to the emitted `question`, scoped to exactly the six
 * types a dynamic section may emit (spec-01 requirement 5). Mirrors the per-type validity checks
 * `SurveySession.tsx` already runs for authored questions, narrowed to this type set.
 */
export function isAnswerComplete(question: DynamicSectionEmittedQuestion, answer: unknown): boolean {
  switch (question.type) {
    case 'open_text':
      return isNonEmptyString(answer);

    case 'single_choice':
      return answer !== null && answer !== undefined && answer !== '';

    case 'multiple_choice':
      return Array.isArray(answer) && answer.length > 0;

    case 'rating_scale':
      return typeof answer === 'number' && Number.isFinite(answer);

    case 'matrix_rating': {
      const rows = (question.type_config?.rows as string[] | undefined) ?? [];
      if (rows.length === 0 || typeof answer !== 'object' || answer === null) return false;
      const given = answer as Record<string, unknown>;
      return rows.every((row) => given[row] !== undefined && given[row] !== null && given[row] !== '');
    }

    case 'percentage_allocation': {
      const options = (question.type_config?.options as string[] | undefined) ?? [];
      const targetSum = (question.type_config?.target_sum as number | undefined) ?? 100;
      if (options.length === 0 || typeof answer !== 'object' || answer === null) return false;
      const given = answer as Record<string, number>;
      const total = options.reduce((sum, option) => sum + (given[option] || 0), 0);
      return total === targetSum;
    }

    default:
      // A dynamic section never emits anything outside the six allowed types (the backend
      // discards and re-asks otherwise), so this is unreached in practice.
      return answer !== null && answer !== undefined && answer !== '';
  }
}
